import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, LogOut } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, ADMIN_TOKEN_KEY } from "@/lib/api";

export function AdminLogout() {
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const logout = async () => {
    try { await api("post", "/admin-auth/logout"); } catch {}
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    qc.removeQueries({ queryKey: ["admin-session"] });
    navigate("/admin/login", { replace: true });
  };
  return (
    <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-sm font-semibold rounded-sm hover:border-[#0A192F]" data-testid="admin-logout-btn">
      <LogOut className="h-4 w-4" /> Log out
    </button>
  );
}

const empty = { current_password: "", new_password: "", confirm: "" };

export function AccountTab() {
  const api = useApi();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      const r = await api("post", "/admin-auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      localStorage.setItem(ADMIN_TOKEN_KEY, r.token);
      toast.success("Password changed");
      setF(empty);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="max-w-md bg-white border border-slate-200 rounded-sm p-6 space-y-4" data-testid="change-password-form">
      <h3 className="font-display font-semibold text-[#0A192F]">Change admin password</h3>
      <div className="space-y-1.5"><Label>Current password</Label><Input type="password" required value={f.current_password} onChange={set("current_password")} data-testid="current-password-input" /></div>
      <div className="space-y-1.5"><Label>New password (at least 10 characters)</Label><Input type="password" required minLength={10} value={f.new_password} onChange={set("new_password")} data-testid="new-password-input" /></div>
      <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" required minLength={10} value={f.confirm} onChange={set("confirm")} data-testid="confirm-password-input" /></div>
      <Button type="submit" disabled={busy} className="bg-[#FF6B00] hover:bg-[#E05D00]" data-testid="change-password-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Update password
      </Button>
    </form>
  );
}
